import React from 'react';
import { Form, Input, Select, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import Icon, { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { EPButton } from '@/components';
import { COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA } from '@/constants/CompressorImageTagData.const';
import { CompressorRowContainer } from './CompressorRow.styles';
import AdditionalInformationCard from '../AdditionalInformationCard/AdditionalInformationCard';
import AdditionalInformationModal from '../AdditionalInformationModal/AdditionalInformationModal';
import { COMPRESSOR_DATA, IMAGES, ROOMS, NO_MATCHING_SPEC } from '@/store/SiteSurveyForm/constants';
import { ImageEditPreview } from './components';
import { MAX_IMAGE_UPLOAD_COUNT } from '../ImageUpload/ImageUpload.consts';
import { CompressorRowProps, getModalTitle } from './CompressorRow.utils';
import { useStores } from '@/hooks/use-stores';
import { useCompressorRow } from './useCompressorRow';

const CompressorRow: React.FC<CompressorRowProps> = ({
  compressor,
  roomId,
  roomIndex,
  index,
  pressureType,
  isMobile,
  isTablet,
  onRemoveCompressor,
  onUpdateManualCompressorField,
  onEditCompressorImageTag,
  onOpenCompressorImageUpload,
  onRemoveCompressorImage,
  renderCompressorNumberInput,
  renderModelSpecSelect,
  compressorsModelSpec,
}) => {
  const { siteSurveyStore } = useStores();
  const shouldRenderLabel = isMobile || isTablet || index === 0;
  const { t } = useTranslation();

  const {
    isVisible,
    currentImageIndex,
    specDataState,
    handleNoMatchingSpecSelect,
    handleAdditionalInfoSave,
    handleEditAdditionalInfo,
    handleModalClose,

    openCompressorImageUpload,
    removeCompressor,
    openEditPreview,
    removeCompressorImage,
    editCompressorImageTag,
    closePreview,
    updateManualCompressorField,
    setCurrentImageIndex,
  } = useCompressorRow({
    compressor,
    roomId,
    roomIndex,
    index,
    pressureType,
    onRemoveCompressor,
    onUpdateManualCompressorField,
    onEditCompressorImageTag,
    onOpenCompressorImageUpload,
    onRemoveCompressorImage,
  });

  const manufacturers = siteSurveyStore.manufacturersList ?? [];

  const manufacturer = Form.useWatch([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'manufacturer']);
  const serialNumber = Form.useWatch([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'serialNumber']);
  const compressorNumber = Form.useWatch([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'number']);
  const currentModelSpec = compressorsModelSpec?.[index]?.modelSpec;

  const renderInputField = (field: string, label: string, required?: boolean) => (
    <div className="compressor-field">
      {shouldRenderLabel && <div className="compressor-label">{t(label)}</div>}
      <Form.Item
        name={[pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, field]}
        className={`compressor-field compressor-field-${field}`}
        rules={[
          {
            required,
            message: '',
          },
        ]}
      >
        <Input
          key={`${roomIndex}-${index}-${field}`}
          placeholder={t(label)}
          value={compressor[field]}
          disabled={!compressor.isManual}
          onChange={e => updateManualCompressorField(field, e.target.value)}
        />
      </Form.Item>
    </div>
  );

  return (
    <CompressorRowContainer>
      {compressor.isManual && (
        <Row>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name={[pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'manufacturer']}
              className="compressor-field compressor-field-manufacturer-type"
              rules={[{ required: true, message: t('Please select a manufacturer') }]}
            >
              <Select
                placeholder={t('Select manufacturer')}
                size="large"
                options={manufacturers}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {(compressor.isManual && manufacturer) || !compressor.isManual ? (
        <div className="compressor-row-content">
          <div className="compressor-field">
            {shouldRenderLabel && <div className="compressor-label">{t('Number')}</div>}
            {renderCompressorNumberInput(roomId, compressor.id, index)}
          </div>

          {renderInputField('serialNumber', 'Serial number', true)}
          {renderInputField('model', 'Model')}

          <div className="compressor-field">
            {shouldRenderLabel && <div className="compressor-label">{t('Model spec')}</div>}
            {renderModelSpecSelect(roomId, compressor.id, index, handleNoMatchingSpecSelect)}
          </div>

          {renderInputField('controller', 'Controller')}

          <div className="compressor-field">
            {shouldRenderLabel && !isTablet && !isMobile && <div className="compressor-label">&nbsp;</div>}
            <EPButton
              className="compressor-action-button"
              onClick={openCompressorImageUpload}
              disabled={compressor.images?.length >= MAX_IMAGE_UPLOAD_COUNT}
              aria-label={t('uploadImageOpenWizzardButton')}
              icon={<UploadOutlined />}
              type={isMobile || isTablet ? 'default' : 'text'}
              size="middle"
            >
              {(isTablet || isMobile) && t('uploadImageOpenWizzardButton')}
            </EPButton>
          </div>

          <div className="compressor-field">
            {shouldRenderLabel && !isTablet && !isMobile && <div className="compressor-label">&nbsp;</div>}
            <EPButton
              className="compressor-action-button"
              onClick={removeCompressor}
              aria-label={t('siteSurveyRemoveCompressor')}
              icon={<CloseOutlined />}
              type="text"
              size="middle"
            >
              {(isTablet || isMobile) && t('siteSurveyRemoveCompressor')}
            </EPButton>
          </div>

          {!!compressor.images?.length && (
            <div className="compressor-field compressor-field-images">
              <Form.Item name={[pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, IMAGES]}>
                <div className="compressor-tags-list">
                  {compressor.images.map((image, index) => (
                    <EPButton
                      key={`${image.id}-${image.tag}`}
                      type="text"
                      size="small"
                      icon={<Icon component={COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[image.tag].Svg} />}
                      onClick={() => openEditPreview(index)}
                    >
                      {t(COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[image.tag].translationKey)}
                    </EPButton>
                  ))}
                </div>
              </Form.Item>

              <ImageEditPreview
                images={compressor.images}
                setCurrentImageIndex={setCurrentImageIndex}
                onRemoveCompressorImage={removeCompressorImage}
                currentImageIndex={currentImageIndex}
                visible={isVisible}
                onEditTag={editCompressorImageTag}
                closePreview={closePreview}
              />
            </div>
          )}
        </div>
      ) : null}

      {currentModelSpec === NO_MATCHING_SPEC &&
        ((compressor.isManual && manufacturer) || !compressor.isManual) &&
        specDataState !== 'url-modal' &&
        specDataState !== 'manual-modal' && (
          <AdditionalInformationCard
            fieldNamePath={[pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'additionalInformation']}
            onEdit={handleEditAdditionalInfo}
          />
        )}

      <AdditionalInformationModal
        open={specDataState === 'url-modal' || specDataState === 'manual-modal'}
        onClose={handleModalClose}
        onSave={handleAdditionalInfoSave}
        title={getModalTitle(serialNumber, manufacturer, compressorNumber, compressor.isManual)}
        fieldNamePath={[pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'additionalInformation']}
        initialMode={specDataState === 'manual-modal' ? 'manual' : 'spec'}
      />
    </CompressorRowContainer>
  );
};

export default CompressorRow;
